import React from 'react';
import { CosmosIcon, LogoIcon, MessageIcon, RecommendationIcon , OceanIcon } from './icons';
import { IpIntelIcon } from './icons/IpIntelIcon';
import { ChallengesIcon } from './icons/ChallengesIcon';
import { WhatsNewIcon } from './icons/WhatsNewIcon';
import { Avatar } from './Avatar';
import { trackClick } from '../utils/analytics';
import { canSeeDirectSearch } from '../utils/featureFlags';
import { UpgradeNavButton } from './upgrade/UpgradePrompts';
import { AdminPreviewBadge } from './AdminPreview';
import { CLOSED_FEATURES, type FeatureMap, type FeatureMode } from '../utils/featureAccess';

interface SideNavProps {
    activeView: string;
    setActiveView: (view: string) => void;
    userName: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    userProfileImage?: string;
    profileCompletionPercentage?: number;
    /** Feature access (utils/featureAccess.ts). Left out = nothing gated is
     *  enabled: no What's New entry, no preview badges (the OCEAN dev harness). */
    features?: FeatureMap;
}

/** Flip to true to bring the Inbox entry back. */
const SHOW_INBOX = false;

export const SideNav: React.FC<SideNavProps> = ({ activeView, setActiveView, userName, userFirstName, userLastName, userEmail, userProfileImage, profileCompletionPercentage = 100, features = CLOSED_FEATURES }) => {
    const displayCompletion = Math.min(profileCompletionPercentage, 100);
    // Cosmos is hidden whenever Direct Search is available (which is the
    // intended product state for now). Code is kept so we can flip
    // SHOW_COSMOS_NAV back on later — we only suppress it from the nav.
    // Users without Direct Search still see Cosmos as a fallback so they
    // have a main app entry point.
    const SHOW_COSMOS_NAV = false;
    const hasDirectSearch = canSeeDirectSearch(userEmail);
    const navItems: { id: string; label: string; icon: React.ReactNode; preview?: boolean;
      previewMode?: FeatureMode }[] = [
        ...(SHOW_COSMOS_NAV || !hasDirectSearch
            ? [{ id: 'cosmos', label: 'Cosmos', icon: <CosmosIcon active={activeView === 'cosmos'} /> }]
            : []),
        // Direct Search is the rebranded "Cosmos" surface. The route id
        // stays `directsearch` to avoid touching the router; only the icon
        // and label change.
        ...(hasDirectSearch
            ? [{ id: 'directsearch', label: 'Cosmos', icon: <CosmosIcon active={activeView === 'directsearch'} /> }]
            : []),
        // Inbox is hidden from the nav per the current spec, NOT removed: the
        // route, the page and the SendGrid inbound-parse threads behind it all
        // still work, so putting the entry back is a one-line revert.
        ...(SHOW_INBOX
            ? [{ id: 'inbox', label: 'Inbox', icon: <MessageIcon active={activeView === 'inbox'} /> }]
            : []),
        // Ocean: OCEAN where feature access enables it, else the Coming Soon
        // page — never hidden, because it is part of what the Voyager plan sells.
        { id: 'ocean', label: 'Ocean', icon: <OceanIcon active={activeView === 'ocean'} />,
          preview: features.ocean.preview, previewMode: features.ocean.mode },
        // Challenges: the Coming Soon page (App.tsx view 'challenges').
        { id: 'challenges', label: 'Challenges', icon: <ChallengesIcon active={activeView === 'challenges'} /> },
        // The public /ip-intel-report page, in the app (App.tsx view 'ipintel').
        { id: 'ipintel', label: 'IP Intel', icon: <IpIntelIcon active={activeView === 'ipintel'} /> },
        // Platform updates + curated IP & licensing news (App.tsx view
        // 'whatsnew') — only where feature access enables it.
        ...(features.whatsnew.enabled
            ? [{ id: 'whatsnew', label: "What's New", icon: <WhatsNewIcon active={activeView === 'whatsnew'} />,
                 preview: features.whatsnew.preview, previewMode: features.whatsnew.mode }]
            : []),
        // For You: the new page where feature access enables it, else the
        // legacy page (App.tsx) — the entry itself is always there.
        { id: 'foryou', label: 'For You', icon: <RecommendationIcon active={activeView === 'foryou'} />,
          preview: features.foryou.preview, previewMode: features.foryou.mode },
    ];
  
    // The top logo navigates to whichever is the primary view for this user —
    // Direct Search if they have access, otherwise Cosmos. Keeps the brand
    // mark functional while Cosmos is hidden from the nav list.
    const primaryView = hasDirectSearch ? 'directsearch' : 'cosmos';

    return (
    <aside className="flex flex-col justify-between items-center self-stretch p-3">
      <div className="flex flex-col items-center gap-4">
        <button
          onClick={() => setActiveView(primaryView)}
          className="w-12 h-[60px] relative cursor-pointer hover:opacity-80 transition-opacity"
          title="Go to Cosmos"
        >
          <div className="flex w-12 h-12 p-1 justify-center items-center absolute top-0 left-0">
            <div className="flex w-[41px] h-[41px] p-[1.117px_1.486px_1.283px_0.914px] justify-center items-center absolute top-1 left-1">
              <LogoIcon />
            </div>
          </div>
          <div className="w-12 h-0 bg-primary-400 absolute left-0 top-[60px]"></div>
        </button>
        <nav className="flex flex-col items-start gap-4">
          {navItems.map(item => (
            <button key={item.id} onClick={() => { trackClick(`nav_click_${item.id}`); setActiveView(item.id); }} className="flex w-12 h-[65px] flex-col items-center gap-[6px]">
                <div className={`relative flex w-12 h-12 flex-col justify-center items-center gap-2.5 ${activeView === item.id ? 'rounded-[8.877px] bg-primary-100 shadow-custom-profile' : ''}`}>
                    {item.icon}
                    {item.preview && <AdminPreviewBadge corner short mode={item.previewMode} />}
                </div>
                {/* Label is always visible; the active item gets the bg+shadow
                    on the icon container above so the highlight still pops. */}
                <div className={`w-12 text-center font-grotesk text-[9px] leading-none ${
                  activeView === item.id ? 'text-grayscale-1300 font-medium' : 'text-grayscale-1100 font-normal'
                }`}>{item.label}</div>
            </button>
          ))}
        </nav>
      </div>
      <div className="flex flex-col items-center gap-3">
      {/* Unpaid accounts only; renders nothing otherwise (admin → Billing →
          Upgrade prompts). */}
      <UpgradeNavButton />
      <button onClick={() => { trackClick('nav_click_profile'); setActiveView('profile'); }} className="flex w-full flex-col justify-center items-center gap-1 py-2">
        <div className="flex flex-col items-center gap-1">
          {/* The ring below still shows progress; the number is internal. */}
          
          <div className={`relative p-1 rounded-full ${activeView === 'profile' ? 'bg-primary-100 shadow-custom-profile' : ''}`}>
              {/* Profile completion progress circle - Always visible */}
              <div className="absolute inset-0 w-full h-full">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  {/* Background circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                    fill="none"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke={displayCompletion >= 100 ? "#D4AF37" : "#EF4444"}
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${displayCompletion / 100 * 283} 283`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
              </div>
              <Avatar 
                firstName={userFirstName} 
                lastName={userLastName} 
                email={userEmail}
                profileImage={userProfileImage}
                size="md"
                className="shadow-sm border border-white relative z-10"
              />
          </div>
        </div>
        <div className="w-12 text-grayscale-1100 text-center font-grotesk text-[10px] font-medium leading-none">Hi {userName}!</div>
      </button>
      </div>
    </aside>
  );
};